import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "../../utils/apparifySwal";
import { ChevronDown, PackageX, Plus, RefreshCw, SlidersHorizontal, Tag, Pencil, X } from "lucide-react";

import Panel from "../components/Panel";

import { businessSwalBase } from "../utils/businessSwal";
import { businessProductsAPI } from "../services/businessProductsService";
import {
  Button,
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  Notice,
  PageHeader,
  SearchInput,
  Sheet,
  SheetClose,
  focusRing,
} from "../../components/ui";

/*  Formatters  */

/** Format integer cents as peso display: 1599 → "₱15.99" */
function centsToPeso(cents) {
  const n = Number(cents || 0);
  return `\u20B1${(n / 100).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format a peso decimal value: 400.5 → "₱400.50" */
function pesoDisplay(n) {
  const v = Number(n || 0);
  return `\u20B1${v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function safeDate(d) {
  if (!d) return "\u2014";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "\u2014";
  return dt.toLocaleDateString("en-PH");
}

/*  Sort keys  */
const SORT_KEYS = {
  name: "name",
  price: "base_price_cents",
  stock: "stock",
  soldQty: "soldQty",
  revenue: "revenue",
  lastSoldAt: "lastSoldAt",
};

/*  Stock classification  */
function classifyStock(stock, threshold = 10) {
  const n = Number(stock ?? 0);
  if (Number.isNaN(n)) return "in";
  if (n <= 0) return "out";
  if (n <= threshold) return "low";
  return "in";
}

/*  StockBadge  */
function StockBadge({ stock, threshold }) {
  const cls = classifyStock(stock, threshold);
  const map = {
    out: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200",
    low: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200",
    in: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200",
  };
  const label = { out: "Out of Stock", low: "Low Stock", in: "In Stock" };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${map[cls]}`}
    >
      {label[cls]}
    </span>
  );
}

/*  ProductStatusBadge  */
const STATUS_STYLES = {
  LISTED: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
  DRAFT: "border-neutral-300 bg-neutral-100 text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  ARCHIVED: "border-neutral-400 bg-neutral-200 text-neutral-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-400",
};
function ProductStatusBadge({ status }) {
  const s = (status || "DRAFT").toUpperCase();
  const label = s === "LISTED" ? "Listed" : s.charAt(0) + s.slice(1).toLowerCase();
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[s] || STATUS_STYLES.DRAFT}`}>
      {label}
    </span>
  );
}

/*  SkeletonLine  */
function SkeletonLine({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-neutral-200 dark:bg-neutral-800 ${className}`}
      aria-hidden="true"
    />
  );
}

const ALL_FILTERS = "__all";

function ProductFilterMenu({ label, value, options, onValueChange, disabled = false }) {
  const menuValue = value || ALL_FILTERS;
  const selected = options.find((option) => option.value === menuValue) || options[0];

  return (
    <div className="space-y-1.5">
      <span className="block text-label text-secondary">{label}</span>
      <Menu>
        <MenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            aria-label={`Filter products by ${label.toLowerCase()}`}
            className={`flex h-control w-full items-center justify-between gap-3 rounded-control border border-default bg-surface px-3 text-left text-body text-foreground transition-colors hover:border-strong disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-tertiary max-md:h-touch ${focusRing}`}
          >
            <span className="min-w-0 truncate">
              {selected.label}{selected.count !== undefined ? ` (${selected.count})` : ""}
            </span>
            <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-tertiary" />
          </button>
        </MenuTrigger>
        <MenuContent
          align="start"
          className="max-h-72 w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
        >
          <MenuRadioGroup
            value={menuValue}
            onValueChange={(nextValue) => onValueChange(nextValue === ALL_FILTERS ? "" : nextValue)}
          >
            {options.map((option) => (
              <MenuRadioItem key={option.value} value={option.value}>
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.count !== undefined ? (
                  <span className="shrink-0 text-meta tabular-nums text-tertiary">{option.count}</span>
                ) : null}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuContent>
      </Menu>
    </div>
  );
}

function ProductFilterFields({
  categories,
  productCount,
  selectedCategoryId,
  stockFilter,
  statusFilter,
  loading,
  onCategoryChange,
  onStockChange,
  onStatusChange,
  className = "",
}) {
  const categoryOptions = [
    { value: ALL_FILTERS, label: "All categories", count: productCount },
    ...categories.map((category) => ({
      value: String(category.id),
      label: category.name,
      count: category.count,
    })),
  ];

  return (
    <div className={className}>
      <ProductFilterMenu
        label="Category"
        value={selectedCategoryId === null ? "" : String(selectedCategoryId)}
        options={categoryOptions}
        onValueChange={(nextValue) => onCategoryChange(nextValue || null)}
        disabled={loading}
      />

      <ProductFilterMenu
        label="Stock"
        value={stockFilter || ""}
        options={[
          { value: ALL_FILTERS, label: "All stock levels" },
          { value: "in", label: "In stock" },
          { value: "low", label: "Low stock" },
          { value: "out", label: "Out of stock" },
        ]}
        onValueChange={(nextValue) => onStockChange(nextValue || null)}
        disabled={loading}
      />

      <ProductFilterMenu
        label="Product status"
        value={statusFilter}
        options={[
          { value: ALL_FILTERS, label: "All statuses" },
          { value: "LISTED", label: "Listed" },
          { value: "DRAFT", label: "Draft" },
          { value: "ARCHIVED", label: "Archived" },
        ]}
        onValueChange={onStatusChange}
        disabled={loading}
      />
    </div>
  );
}

/*  Main Component  */
export default function BusinessProducts() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const swalBase = useMemo(
    () => ({ ...businessSwalBase, target: "#business-swal-root" }),
    []
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState(SORT_KEYS.revenue);
  const [sortDir, setSortDir] = useState("desc");

  /* active category/stock filter from URL */
  const categoryParam = params.get("category");
  const selectedCategoryId =
    categoryParam === "uncategorized"
      ? "uncategorized"
      : categoryParam
      ? Number(categoryParam) || null
      : null;
  const stockFilter = params.get("stock");
  const statusParam = String(params.get("status") || "").toUpperCase();
  const statusFilter = ["LISTED", "DRAFT", "ARCHIVED"].includes(statusParam)
    ? statusParam
    : "";

  /*  Load both reports + categories in parallel  */
  const load = useCallback(
    async ({ withSwal = false } = {}) => {
      setLoading(true);
      setError("");

      if (withSwal) {
        Swal.fire({
          ...swalBase,
          title: "Loading",
          html: "Fetching product sales report",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => Swal.showLoading(),
        });
      }

      try {
        const [salesResult, catResult] = await Promise.allSettled([
          businessProductsAPI.getProductSalesReport(),
          businessProductsAPI.getCategories(),
        ]);

        /* Normalise products array */
        const raw =
          salesResult.status === "fulfilled" ? salesResult.value : null;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data?.items)
          ? raw.data.items
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.items)
          ? raw.items
          : [];
        setRows(list);

        /* Normalise categories array */
        const catRaw =
          catResult.status === "fulfilled" ? catResult.value : [];
        const catList = Array.isArray(catRaw)
          ? catRaw
          : Array.isArray(catRaw?.data)
          ? catRaw.data
          : [];
        setCategories(catList);

        if (withSwal) Swal.close();
      } catch (e) {
        if (withSwal) Swal.close();
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load product sales report.";
        setError(msg);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [swalBase]
  );

  useEffect(() => {
    load();
  }, [load]);

  /*  Enrich categories with product counts + Uncategorized entry  */
  const enrichedCategories = useMemo(() => {
    const countMap = new Map();
    let uncategorizedCount = 0;
    rows.forEach((p) => {
      if (p.category_id != null) {
        // Normalise to Number — MySQL2 may return the column as string or number
        const cid = Number(p.category_id);
        countMap.set(cid, (countMap.get(cid) || 0) + 1);
      } else {
        uncategorizedCount++;
      }
    });
    // Deduplicate by ID before enriching
    const seen = new Set();
    const cats = categories
      .filter((c) => {
        const cid = Number(c.id);
        if (seen.has(cid)) return false;
        seen.add(cid);
        return true;
      })
      .map((c) => ({ ...c, id: Number(c.id), count: countMap.get(Number(c.id)) || 0 }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    /* Append Uncategorized if any products lack a category */
    if (uncategorizedCount > 0) {
      cats.push({ id: "uncategorized", name: "Uncategorized", count: uncategorizedCount });
    }
    return cats;
  }, [categories, rows]);

  /*  Rows scoped to selected category  */
  const byCategory = useMemo(() => {
    if (selectedCategoryId === null) return rows;
    if (selectedCategoryId === "uncategorized")
      return rows.filter((p) => p.category_id == null);
    // Compare as numbers — MySQL2 may return category_id as a string
    return rows.filter((p) => Number(p.category_id) === selectedCategoryId);
  }, [rows, selectedCategoryId]);

  /*  Apply stock, status, and search filters  */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = byCategory;
    if (stockFilter === "low" || stockFilter === "out" || stockFilter === "in") {
      base = base.filter(
        (p) =>
          classifyStock(p.stock, p.low_stock_threshold ?? 10) === stockFilter
      );
    }
    if (statusFilter) {
      base = base.filter(
        (p) => String(p.status || "DRAFT").toUpperCase() === statusFilter
      );
    }
    if (q) {
      base = base.filter((p) =>
        `${p.name || ""} ${p.id || ""}`.toLowerCase().includes(q)
      );
    }
    return base;
  }, [byCategory, query, stockFilter, statusFilter]);

  /*  Sort  */
  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (p) => {
      if (sortBy === SORT_KEYS.name) return String(p.name || "");
      if (sortBy === SORT_KEYS.lastSoldAt)
        return p.lastSoldAt ? new Date(p.lastSoldAt).getTime() : 0;
      return Number(p[sortBy] ?? 0);
    };
    return [...filtered].sort((a, b) => {
      const av = getVal(a),
        bv = getVal(b);
      if (typeof av === "string" || typeof bv === "string")
        return String(av).localeCompare(String(bv)) * dir;
      return (av - bv) * dir;
    });
  }, [filtered, sortBy, sortDir]);

  /*  Filter helpers  */
  function toggleSort(key) {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("desc"); }
  }
  function sortIndicator(key) {
    return sortBy !== key ? "" : sortDir === "asc" ? " \u25B2" : " \u25BC";
  }

  const setStockFilter = useCallback((v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete("stock");
    else next.set("stock", v);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const setCategoryFilter = useCallback((id) => {
    const next = new URLSearchParams(params);
    if (id === null || id === undefined) next.delete("category");
    else next.set("category", String(id));
    setParams(next, { replace: true });
  }, [params, setParams]);

  const setStatusFilter = useCallback((status) => {
    const next = new URLSearchParams(params);
    if (!status) next.delete("status");
    else next.set("status", status);
    setParams(next, { replace: true });
  }, [params, setParams]);

  function clearFilters() {
    const next = new URLSearchParams(params);
    next.delete("stock");
    next.delete("category");
    next.delete("status");
    setParams(next, { replace: true });
    setQuery("");
  }

  const selectedCategoryName = useMemo(
    () =>
      selectedCategoryId === "uncategorized"
        ? "Uncategorized"
        : selectedCategoryId !== null
        ? enrichedCategories.find((c) => c.id === selectedCategoryId)?.name || ""
        : "",
    [selectedCategoryId, enrichedCategories]
  );

  const stockBadgeLabel = useMemo(() => {
    if (stockFilter === "low") return "Low stock";
    if (stockFilter === "out") return "Out of stock";
    if (stockFilter === "in") return "In stock";
    return "";
  }, [stockFilter]);

  const statusBadgeLabel = statusFilter
    ? statusFilter.charAt(0) + statusFilter.slice(1).toLowerCase()
    : "";
  const hasActiveFilters = Boolean(
    query.trim() || selectedCategoryId !== null || stockFilter || statusFilter
  );
  const activeFilterCount = Number(selectedCategoryId !== null)
    + Number(Boolean(stockFilter))
    + Number(Boolean(statusFilter));

  const merchandisingAttentionCount = useMemo(
    () => rows.filter((product) => (
      String(product.status || "").toUpperCase() === "DRAFT"
      || product.category_id == null
      || !product.image
    )).length,
    [rows],
  );

  const thBase =
    "p-3 text-xs font-semibold text-mnla-muted dark:text-mnla-darkMuted cursor-pointer select-none whitespace-nowrap";
  const tdBase =
    "p-3 text-sm text-mnla-text dark:text-mnla-darkText whitespace-nowrap";

  /*  Render  */
  return (
    <div className="space-y-6" id="business-swal-root">
      {/* The page title comes first (audit §8: content rendered above it). */}
      <PageHeader
        title="Products"
      />
      {!loading && !error && merchandisingAttentionCount > 0 && (
        <Notice
          tone="warning"
          title={`${merchandisingAttentionCount.toLocaleString("en-PH")} ${merchandisingAttentionCount === 1 ? "product needs" : "products need"} attention`}
          className="max-sm:flex-col max-sm:[&>div:last-child]:w-full max-sm:[&>div:last-child]:self-stretch"
          action={(
            <Button
              size="sm"
              variant="warning"
              className="max-sm:w-full"
              onClick={() => navigate("/business/merchandising/open-work")}
            >
              Review open work
            </Button>
          )}
        >
          Draft, category, or media issues are preventing catalog readiness.
        </Notice>
      )}
      <Panel
        title="All products"
        right={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex items-center gap-2">
              <Button size="sm" icon={RefreshCw} onClick={() => load({ withSwal: true })}>
                Refresh
              </Button>
              <Button size="sm" variant="primary" icon={Plus} onClick={() => navigate("/business/products/add")}>
                Add Product
              </Button>
            </div>
            <SearchInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or ID"
              label="Search products by name or ID"
              className="w-full sm:w-72"
            />
          </div>
        }
      >
        <div className="mb-4 space-y-3 border-b border-subtle pb-4">
          <ProductFilterFields
            categories={enrichedCategories}
            productCount={rows.length}
            selectedCategoryId={selectedCategoryId}
            stockFilter={stockFilter}
            statusFilter={statusFilter}
            loading={loading}
            onCategoryChange={setCategoryFilter}
            onStockChange={setStockFilter}
            onStatusChange={setStatusFilter}
            className="hidden gap-3 sm:grid sm:grid-cols-3"
          />

          <div className="flex items-center justify-between gap-3 sm:hidden">
            <p className="text-body-sm text-secondary" aria-live="polite">
              {loading
                ? "Loading products…"
                : `Showing ${sorted.length.toLocaleString("en-PH")} of ${rows.length.toLocaleString("en-PH")} products`}
            </p>
            <Button size="sm" icon={SlidersHorizontal} onClick={() => setFiltersOpen(true)}>
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
          </div>

          <div className="hidden min-h-control-sm items-center justify-between gap-2 sm:flex">
            <p className="text-body-sm text-secondary" aria-live="polite">
              {loading
                ? "Loading products…"
                : `Showing ${sorted.length.toLocaleString("en-PH")} of ${rows.length.toLocaleString("en-PH")} products`}
            </p>
            {hasActiveFilters ? (
              <Button size="sm" variant="quiet" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null}
          </div>
        </div>

        {(selectedCategoryId !== null || stockFilter || statusFilter) && (
          <div className="mb-4 flex flex-wrap items-center gap-2" aria-label="Active filters">
            {selectedCategoryId !== null && (
              <span className="inline-flex min-h-control-sm items-center gap-1.5 rounded-full bg-accent-subtle px-3 text-control-sm text-accent">
                <Tag aria-hidden="true" className="h-3.5 w-3.5" />
                {selectedCategoryName}
                <button
                  type="button"
                  onClick={() => setCategoryFilter(null)}
                  className="ml-0.5 rounded-full p-1 hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                  aria-label="Remove category filter"
                >
                  <X aria-hidden="true" className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            {stockFilter && (
              <span className="inline-flex min-h-control-sm items-center gap-1.5 rounded-full bg-accent-subtle px-3 text-control-sm text-accent">
                {stockBadgeLabel}
                <button
                  type="button"
                  onClick={() => setStockFilter(null)}
                  className="ml-0.5 rounded-full p-1 hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                  aria-label="Remove stock filter"
                >
                  <X aria-hidden="true" className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex min-h-control-sm items-center gap-1.5 rounded-full bg-accent-subtle px-3 text-control-sm text-accent">
                {statusBadgeLabel}
                <button
                  type="button"
                  onClick={() => setStatusFilter("")}
                  className="ml-0.5 rounded-full p-1 hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                  aria-label="Remove product status filter"
                >
                  <X aria-hidden="true" className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </div>
        )}

        {/*  PRODUCT TABLE / EMPTY STATE  */}
        {loading ? (
          <div className="space-y-3 rounded-2xl border border-mnla-border bg-mnla-card p-4 dark:border-mnla-darkBorder dark:bg-mnla-darkCard">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonLine key={i} className="h-4 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
            {error}
          </div>
        ) : sorted.length === 0 ? (
          <div className="rounded-2xl border border-mnla-border bg-mnla-card p-6 text-center dark:border-mnla-darkBorder dark:bg-mnla-darkCard">
            <PackageX className="mx-auto h-10 w-10 text-neutral-300 dark:text-neutral-700" />
            <p className="mt-2 text-sm text-mnla-muted dark:text-mnla-darkMuted">
              {rows.length === 0
                ? "No products yet. Add your first product!"
                : "No products match the current search and filters."}
            </p>
            {rows.length === 0 && (
              <Button
                className="mt-3"
                variant="primary"
                icon={Plus}
                onClick={() => navigate("/business/products/add")}
              >
                Add Product
              </Button>
            )}
            {rows.length > 0 && hasActiveFilters && (
              <Button className="mt-3" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE */}
            <div className="hidden sm:block overflow-x-auto rounded-2xl border border-mnla-border dark:border-mnla-darkBorder">
              <table className="w-full min-w-[860px] text-left border-collapse bg-mnla-card dark:bg-mnla-darkCard">
                <thead className="bg-neutral-50 dark:bg-neutral-900/40">
                  <tr className="border-b border-mnla-border dark:border-mnla-darkBorder">
                    <th
                      className={thBase}
                      onClick={() => toggleSort(SORT_KEYS.name)}
                    >
                      Product{sortIndicator(SORT_KEYS.name)}
                    </th>
                    <th className={thBase}>
                      Category
                    </th>
                    <th
                      className={thBase}
                      onClick={() => toggleSort(SORT_KEYS.price)}
                    >
                      Price{sortIndicator(SORT_KEYS.price)}
                    </th>
                    <th
                      className={thBase}
                      onClick={() => toggleSort(SORT_KEYS.stock)}
                    >
                      Stock{sortIndicator(SORT_KEYS.stock)}
                    </th>
                    <th
                      className={thBase}
                      onClick={() => toggleSort(SORT_KEYS.soldQty)}
                    >
                      Sold{sortIndicator(SORT_KEYS.soldQty)}
                    </th>
                    <th
                      className={thBase}
                      onClick={() => toggleSort(SORT_KEYS.revenue)}
                    >
                      Revenue{sortIndicator(SORT_KEYS.revenue)}
                    </th>
                    <th
                      className={thBase}
                      onClick={() => toggleSort(SORT_KEYS.lastSoldAt)}
                    >
                      Last Sold{sortIndicator(SORT_KEYS.lastSoldAt)}
                    </th>
                    <th className={thBase}>Status</th>
                    <th className={`${thBase} text-center`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p) => {
                    const sc = classifyStock(
                      p.stock,
                      p.low_stock_threshold ?? 10
                    );
                    const tone =
                      sc === "out"
                        ? "text-rose-700 dark:text-rose-200 font-semibold"
                        : sc === "low"
                        ? "text-amber-800 dark:text-amber-200 font-semibold"
                        : "text-mnla-text dark:text-mnla-darkText";
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-mnla-border hover:bg-neutral-50 dark:border-mnla-darkBorder dark:hover:bg-neutral-900/30 cursor-pointer"
                        onClick={() =>
                          navigate(`/business/products/${p.id}`)
                        }
                      >
                        <td className="p-3 max-w-[200px]">
                          <div
                            className="text-sm font-semibold text-mnla-text dark:text-mnla-darkText truncate"
                            title={p.name || "\u2014"}
                          >
                            {p.name || "\u2014"}
                          </div>
                          <div className="text-xs text-mnla-muted dark:text-mnla-darkMuted">
                            ID: {p.id}
                          </div>
                        </td>
                        <td className="p-3">
                          {p.category_name ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCategoryFilter(p.category_id);
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-600 hover:border-mnla-accent hover:text-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 transition"
                            >
                              <Tag size={9} />
                              {p.category_name}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCategoryFilter("uncategorized");
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-dashed border-neutral-300 px-2.5 py-0.5 text-[11px] font-medium text-neutral-400 hover:border-mnla-accent hover:text-mnla-accent dark:border-neutral-700 dark:text-neutral-500 transition"
                            >
                              Uncategorized
                            </button>
                          )}
                        </td>
                        <td className={tdBase}>{centsToPeso(p.base_price_cents)}</td>
                        <td
                          className={`p-3 text-sm whitespace-nowrap tabular-nums ${tone}`}
                        >
                          {p.stock ?? "\u2014"}
                        </td>
                        <td className={`${tdBase} tabular-nums`}>
                          {(p.soldQty ?? 0).toLocaleString("en-PH")}
                        </td>
                        <td className={`${tdBase} tabular-nums`}>
                          {pesoDisplay(p.revenue)}
                        </td>
                        <td className={tdBase}>{safeDate(p.lastSoldAt)}</td>
                        <td className="p-3"><ProductStatusBadge status={p.status} /></td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/business/products/${p.id}`);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-mnla-border px-2.5 py-1.5 text-xs font-medium text-mnla-text hover:bg-neutral-50 dark:border-mnla-darkBorder dark:text-mnla-darkText dark:hover:bg-neutral-900/60"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE CARDS */}
            <div className="sm:hidden space-y-3">
              {sorted.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`/business/products/${p.id}`)}
                  className="w-full text-left rounded-2xl border border-mnla-border bg-mnla-card p-4 shadow-sm dark:border-mnla-darkBorder dark:bg-mnla-darkCard active:scale-[0.99] transition-transform"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-mnla-text dark:text-mnla-darkText truncate"
                        title={p.name || "Untitled"}
                      >
                        {p.name || "Untitled"}
                      </div>
                      <div className="text-xs text-mnla-muted dark:text-mnla-darkMuted">
                        ID: {p.id} · {centsToPeso(p.base_price_cents)}
                      </div>
                      {p.category_name && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-neutral-500 dark:text-neutral-400">
                          <Tag size={9} />
                          {p.category_name}
                        </div>
                      )}
                    </div>
                    <StockBadge
                      stock={p.stock}
                      threshold={p.low_stock_threshold ?? 10}
                    />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <ProductStatusBadge status={p.status} />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[11px] text-mnla-muted dark:text-mnla-darkMuted">
                        Stock
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-mnla-text dark:text-mnla-darkText">
                        {p.stock ?? 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-mnla-muted dark:text-mnla-darkMuted">
                        Sold
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-mnla-text dark:text-mnla-darkText">
                        {(p.soldQty ?? 0).toLocaleString("en-PH")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-mnla-muted dark:text-mnla-darkMuted">
                        Revenue
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-mnla-text dark:text-mnla-darkText">
                        {pesoDisplay(p.revenue)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}


      </Panel>

      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen} title="Filter products">
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <ProductFilterFields
              categories={enrichedCategories}
              productCount={rows.length}
              selectedCategoryId={selectedCategoryId}
              stockFilter={stockFilter}
              statusFilter={statusFilter}
              loading={loading}
              onCategoryChange={setCategoryFilter}
              onStockChange={setStockFilter}
              onStatusChange={setStatusFilter}
              className="grid gap-4"
            />
          </div>
          <div className="space-y-2 border-t border-subtle p-4">
            {activeFilterCount > 0 ? (
              <Button className="w-full" variant="quiet" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null}
            <SheetClose asChild>
              <Button className="w-full" variant="primary">
                Show {sorted.length.toLocaleString("en-PH")} {sorted.length === 1 ? "product" : "products"}
              </Button>
            </SheetClose>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
